(() => {
  'use strict';

  const instances = new WeakMap();
  const steps = [
    { id: 'kd-field-color', label: 'Fabric' },
    { id: 'kd-field-size', label: 'Size' },
    { id: 'kd-review', label: 'Review' }
  ];
  const typing = element => {
    if (!element) return false;
    if (element.isContentEditable) return true;
    if (element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') return true;
    return element.tagName === 'INPUT' && !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'hidden', 'image'].includes(element.type);
  };

  class DraperyFocus {
    constructor(root) {
      this.root = root;
      this.nav = root.querySelector('.kd-flow');
      this.form = root.querySelector('[data-drapery-form]');
      this.add = root.querySelector('[data-add]');
      this.total = root.querySelector('[data-total-price]');
      this.message = root.querySelector('[data-message]');
      if (!this.nav || !this.form || !this.add || !this.total) return;
      this.links = steps.map(step => this.nav.querySelector(`a[href="#${step.id}"]`));
      this.progress = this.nav.querySelector('[data-kd-focus-progress]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.bar = document.createElement('div');
      this.bar.className = 'kd-focus-bar';
      this.bar.setAttribute('role', 'region');
      this.bar.setAttribute('aria-label', 'Drapery summary');
      this.bar.hidden = true;
      const priceGroup = document.createElement('div');
      const caption = document.createElement('span');
      caption.className = 'kd-focus-caption';
      const currency = String(root.dataset.currency || 'USD').trim().toUpperCase();
      caption.textContent = /^[A-Z]{3}$/.test(currency) ? `Total · ${currency}` : 'Total';
      this.price = document.createElement('strong');
      this.price.className = 'kd-focus-price';
      this.price.textContent = '—';
      priceGroup.append(caption, this.price);
      this.review = document.createElement('a');
      this.review.className = 'kd-focus-review';
      this.review.href = '#kd-review';
      this.review.textContent = 'Review details';
      this.bar.append(priceGroup, this.review);
      root.append(this.bar);
      root.dataset.focusReady = 'true';
      this.queue = () => this.schedule();
      this.onNavigate = event => {
        const link = event.target.closest('a[href^="#kd-"]');
        if (!link || !(this.nav.contains(link) || link === this.review)) return;
        const index = steps.findIndex(step => link.getAttribute('href') === `#${step.id}`);
        const target = index >= 0 ? root.querySelector(`#${steps[index].id}`) : null;
        if (!target || target.hidden || target.getBoundingClientRect().height <= 0) return;
        event.preventDefault();
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'start', behavior: this.reduced.matches ? 'auto' : 'smooth' });
        this.setCurrent(index);
        this.schedule();
      };
      this.nav.addEventListener('click', this.onNavigate);
      this.review.addEventListener('click', this.onNavigate);
      window.addEventListener('scroll', this.queue, { passive: true });
      window.addEventListener('resize', this.queue);
      document.addEventListener('focusin', this.queue);
      document.addEventListener('focusout', this.queue);
      window.visualViewport?.addEventListener('resize', this.queue);
      window.visualViewport?.addEventListener('scroll', this.queue);
      this.observer = new MutationObserver(this.queue);
      this.observer.observe(this.form, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'class'] });
      if (typeof ResizeObserver !== 'undefined') {
        this.sizes = new ResizeObserver(this.queue);
        this.sizes.observe(this.nav);
        this.sizes.observe(this.form);
      }
      this.render();
    }

    setCurrent(index) {
      this.links.forEach((link, position) => {
        if (!link) return;
        if (position === index) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(index + 1));
        this.progress.setAttribute('aria-valuetext', steps[index].label);
      }
      this.nav.style.setProperty('--kd-focus-progress', `${(index + 1) * 100 / steps.length}%`);
    }

    schedule() {
      if (this.stopped || this.frame) return;
      this.frame = window.requestAnimationFrame(() => { this.frame = null; this.render(); });
    }

    render() {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const navRect = this.nav.getBoundingClientRect();
      this.root.style.setProperty('--kd-focus-nav-height', `${Math.ceil(navRect.height)}px`);
      // Match the rounded CSS scroll margin, allowing subpixel anchor placement.
      const threshold = Math.ceil(Math.max(navRect.bottom, navRect.height)) + 20;
      let current = 0;
      steps.forEach((step, index) => {
        const target = this.root.querySelector(`#${step.id}`);
        if (!target || target.hidden) return;
        const rect = target.getBoundingClientRect();
        if (rect.height > 0 && rect.top <= threshold) current = index;
      });
      this.setCurrent(current);
      const total = this.total.textContent.trim() || '—';
      if (this.price.textContent !== total) this.price.textContent = total;
      const needsReview = this.add.disabled || this.message?.classList.contains('is-error');
      const label = needsReview ? 'Review details' : 'Review';
      if (this.review.textContent !== label) this.review.textContent = label;
      const addRect = this.add.getBoundingClientRect();
      const addVisible = !this.add.hidden && addRect.height > 0 && addRect.bottom > 0 && addRect.top < height;
      const rootRect = this.root.getBoundingClientRect();
      const keyboard = typing(document.activeElement) || Boolean(viewport && (viewport.scale > 1.05 || window.innerHeight - viewport.height > Math.max(120, window.innerHeight * .2)));
      const show = this.mobile.matches && navRect.top < height && rootRect.bottom > 0 && !addVisible && !keyboard;
      this.bar.hidden = !show;
    }

    destroy() {
      this.stopped = true;
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.sizes?.disconnect();
      window.removeEventListener('scroll', this.queue);
      window.removeEventListener('resize', this.queue);
      document.removeEventListener('focusin', this.queue);
      document.removeEventListener('focusout', this.queue);
      window.visualViewport?.removeEventListener('resize', this.queue);
      window.visualViewport?.removeEventListener('scroll', this.queue);
      this.nav.removeEventListener('click', this.onNavigate);
      this.review.removeEventListener('click', this.onNavigate);
      this.bar.remove();
      delete this.root.dataset.focusReady;
    }
  }

  function initializeAll() {
    document.querySelectorAll('kalia-drapery').forEach(root => {
      if (root.dataset.focusReady === 'true') return;
      const instance = new DraperyFocus(root);
      if (root.dataset.focusReady === 'true') instances.set(root, instance);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('kalia-drapery').forEach(root => { instances.get(root)?.destroy(); instances.delete(root); });
  });
})();
